import type { SortDirection } from "@/components/base/column-filter-popover";

function matchesFilter(cellValue: string, filterValue: string, exact: boolean): boolean {
    const cell = cellValue.toLowerCase();
    const filter = filterValue.toLowerCase();
    return exact ? cell === filter : cell.includes(filter);
}

export function filterPolicyRows<T, TColumnKey extends string>(
    rows: T[],
    getCellValue: (row: T, column: TColumnKey) => string,
    activeFilters: Partial<Record<TColumnKey, string>>,
    exactMatchColumns: Set<TColumnKey> = new Set(),
): T[] {
    const entries = Object.entries(activeFilters) as [TColumnKey, string][];
    if (entries.length === 0) return rows;

    return rows.filter((row) =>
        entries.every(([column, value]) => {
            if (!value) return true;
            const cellValue = getCellValue(row, column);
            return matchesFilter(cellValue, value, exactMatchColumns.has(column));
        }),
    );
}

export function sortPolicyRows<T, TColumnKey extends string>(
    rows: T[],
    getCellValue: (row: T, column: TColumnKey) => string,
    sort: { by: TColumnKey; order: SortDirection } | null,
    getSortValue?: (row: T, column: TColumnKey) => string | number,
): T[] {
    if (!sort) return rows;

    const direction = sort.order === "asc" ? 1 : -1;
    const getValue = getSortValue ?? getCellValue;

    return [...rows].sort((a, b) => {
        const aValue = getValue(a, sort.by);
        const bValue = getValue(b, sort.by);
        if (typeof aValue === "number" && typeof bValue === "number") {
            const aInvalid = Number.isNaN(aValue);
            const bInvalid = Number.isNaN(bValue);
            if (aInvalid && bInvalid) return 0;
            if (aInvalid) return 1;
            if (bInvalid) return -1;
            return direction * (aValue - bValue);
        }
        return direction * String(aValue).localeCompare(String(bValue), undefined, { sensitivity: "base", numeric: true });
    });
}

export function processPolicyRows<T, TColumnKey extends string>(
    rows: T[],
    getCellValue: (row: T, column: TColumnKey) => string,
    activeFilters: Partial<Record<TColumnKey, string>>,
    sort: { by: TColumnKey; order: SortDirection } | null,
    exactMatchColumns: Set<TColumnKey> = new Set(),
    getSortValue?: (row: T, column: TColumnKey) => string | number,
): T[] {
    return sortPolicyRows(filterPolicyRows(rows, getCellValue, activeFilters, exactMatchColumns), getCellValue, sort, getSortValue);
}
